import React, { useEffect, useState, useRef } from 'react';
import QRCode from 'qrcode';
import API from '../api';
import { compressImage } from '../utils';
import { IcPlus, IcTrash, IcUsers, IcX, IcQr, IcDownload, IcRefresh } from './Icons';

const emptyForm = { first_name: '', last_name: '', class_name: '', parent_name: '', parent_email: '', parent_phone: '' };

function QrModal({ student, onClose, onRegenerate }) {
  const [qrUrl, setQrUrl] = useState('');
  const pin = student.parent_pin;
  const pinDisplay = pin ? `${pin.slice(0,3)} ${pin.slice(3)}` : '— — — —';

  useEffect(() => {
    if (!pin) return;
    QRCode.toDataURL(pin, {
      width: 220, margin: 2,
      color: { dark: '#0F172A', light: '#FFFFFF' },
    }).then(setQrUrl);
  }, [pin]);

  const downloadQr = () => {
    if (!qrUrl) return;
    const a = document.createElement('a');
    a.href = qrUrl;
    a.download = `code-parent-${student.first_name}-${student.last_name}.png`;
    a.click();
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:'#fff', borderRadius:20, padding:32, width:340, boxShadow:'0 24px 64px rgba(0,0,0,0.18)', position:'relative' }}>
        <button onClick={onClose} style={{ position:'absolute', top:16, right:16, background:'var(--slate-100)', border:'none', borderRadius:8, width:30, height:30, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
          <IcX size={14} stroke="var(--slate-500)" />
        </button>

        <div style={{ textAlign:'center', marginBottom:20 }}>
          <div style={{ fontWeight:700, fontSize:16, color:'var(--slate-800)', marginBottom:4 }}>
            {student.first_name} {student.last_name}
          </div>
          <div style={{ fontSize:12, color:'var(--slate-400)' }}>Code d'accès parent</div>
        </div>

        {pin ? (
          <>
            {/* QR Code */}
            <div style={{ display:'flex', justifyContent:'center', marginBottom:20 }}>
              {qrUrl
                ? <img src={qrUrl} alt="QR Code" style={{ borderRadius:12, border:'4px solid var(--slate-100)' }} />
                : <div style={{ width:220, height:220, background:'var(--slate-100)', borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--slate-300)', fontSize:12 }}>Génération...</div>}
            </div>

            {/* PIN */}
            <div style={{ background:'var(--slate-50)', border:'1px solid var(--slate-200)', borderRadius:12, padding:'14px 20px', textAlign:'center', marginBottom:16 }}>
              <div style={{ fontSize:11, fontWeight:600, color:'var(--slate-400)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6 }}>Code à 6 chiffres</div>
              <div style={{ fontSize:36, fontWeight:800, color:'var(--slate-800)', letterSpacing:'0.2em', fontVariantNumeric:'tabular-nums' }}>
                {pinDisplay}
              </div>
              <div style={{ fontSize:11, color:'var(--slate-400)', marginTop:6 }}>Le parent saisit ce code dans l'application</div>
            </div>

            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-primary" style={{ flex:1, justifyContent:'center', fontSize:13 }} onClick={downloadQr}>
                <IcDownload size={14} strokeWidth={2} />
                Télécharger QR
              </button>
              <button className="btn btn-ghost" style={{ fontSize:12, padding:'0 12px' }} onClick={onRegenerate} title="Générer un nouveau code">
                <IcRefresh size={14} strokeWidth={2} />
              </button>
            </div>

            <div style={{ marginTop:14, padding:'10px 14px', background:'rgba(99,102,241,0.06)', borderRadius:10, border:'1px solid rgba(99,102,241,0.15)' }}>
              <div style={{ fontSize:11, color:'var(--slate-500)', lineHeight:1.7 }}>
                <strong style={{ color:'var(--indigo)' }}>Comment partager :</strong><br/>
                Envoyez le QR code ou le code à 6 chiffres au parent par WhatsApp ou SMS. Le parent le saisit une seule fois dans l'application.
              </div>
            </div>
          </>
        ) : (
          <div style={{ textAlign:'center', padding:'20px 0' }}>
            <div style={{ fontSize:13, color:'var(--slate-500)', marginBottom:16 }}>Aucun code généré pour cet élève</div>
            <button className="btn btn-primary" onClick={onRegenerate}>
              <IcQr size={15} strokeWidth={2} />
              Générer le code parent
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Students() {
  const [students, setStudents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [qrModal, setQrModal] = useState(null);
  const [newStudentCode, setNewStudentCode] = useState(null);
  const fileRef = useRef();

  const load = () => API.get('/students/school/1').then(r => setStudents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!fileRef.current?.files[0]) return setMsg('Photo requise');
    setLoading(true); setMsg('');
    const compressed = await compressImage(fileRef.current.files[0]);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append('school_id', 1);
    fd.append('photo', compressed, 'photo.jpg');
    try {
      const r = await API.post('/students/register', fd);
      setForm(emptyForm);
      fileRef.current.value = '';
      setShowForm(false);
      await load();
      // Afficher le code parent généré automatiquement
      if (r.data.parent_pin) {
        setNewStudentCode({
          name: `${form.first_name} ${form.last_name}`,
          first_name: form.first_name,
          last_name: form.last_name,
          parent_pin: r.data.parent_pin,
          id: r.data.student_id,
          class_name: form.class_name,
        });
      }
    } catch (e) { setMsg(e.response?.data?.detail || 'Erreur'); }
    setLoading(false);
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer cet élève ?')) return;
    await API.delete(`/students/${id}`);
    load();
  };

  const regenerate = async (student) => {
    try {
      const r = await API.post(`/students/${student.id}/generate-parent-key`);
      await load();
      setQrModal(s => s ? { ...s, parent_pin: r.data.parent_pin } : null);
    } catch {}
  };

  const openModal = (student) => setQrModal(student);

  const F = ({ label, k, type = 'text' }) => (
    <div>
      <label className="label">{label}</label>
      <input className="input" type={type} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
    </div>
  );

  return (
    <div style={{ padding: 28 }}>
      {/* Modal QR après inscription */}
      {newStudentCode && (
        <QrModal
          student={newStudentCode}
          onClose={() => setNewStudentCode(null)}
          onRegenerate={() => regenerate(newStudentCode)}
        />
      )}

      {/* Modal QR existant */}
      {qrModal && (
        <QrModal
          student={qrModal}
          onClose={() => setQrModal(null)}
          onRegenerate={async () => {
            await regenerate(qrModal);
            const updated = students.find(s => s.id === qrModal.id);
            if (updated) setQrModal({ ...updated });
          }}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 className="page-title">Élèves</h1>
          <p className="page-subtitle">{students.length} élève{students.length > 1 ? 's' : ''} enregistré{students.length > 1 ? 's' : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          <IcPlus size={15} strokeWidth={2.5} /> Ajouter
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ padding: 24, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--slate-800)' }}>Nouvel élève</div>
            <button className="btn btn-ghost btn-icon" onClick={() => setShowForm(false)}><IcX size={15} /></button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
            <F label="Prénom" k="first_name" />
            <F label="Nom de famille" k="last_name" />
            <F label="Classe" k="class_name" />
            <F label="Nom du parent" k="parent_name" />
            <F label="Email parent" k="parent_email" type="email" />
            <F label="Téléphone parent" k="parent_phone" type="tel" />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label className="label">Photo de l'élève</label>
            <input ref={fileRef} type="file" accept="image/*" className="input" style={{ paddingTop: 6 }} />
          </div>
          {msg && msg !== 'ok' && (
            <div style={{ padding: '8px 12px', background: 'var(--red-l)', color: 'var(--red)', borderRadius: 7, fontSize: 12, marginBottom: 12 }}>{msg}</div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={submit} disabled={loading}>
              {loading ? 'Enregistrement...' : "Enregistrer l'élève"}
            </button>
            <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Annuler</button>
          </div>
        </div>
      )}

      <div className="card" style={{ overflow: 'hidden' }}>
        {students.length === 0 ? (
          <div className="empty">
            <IcUsers size={40} stroke="var(--slate-300)" />
            <p style={{ marginTop: 12 }}>Aucun élève enregistré</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Élève</th>
                <th>Classe</th>
                <th>Parent</th>
                <th>Contact</th>
                <th>Code parent</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {students.map(s => (
                <tr key={s.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {s.photo_url
                        ? <img src={s.photo_url} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid var(--slate-200)' }} />
                        : <div className="avatar av-md av-indigo">{s.first_name[0]}{s.last_name[0]}</div>
                      }
                      <div style={{ fontWeight: 600, color: 'var(--slate-800)', fontSize: 13 }}>{s.first_name} {s.last_name}</div>
                    </div>
                  </td>
                  <td><span className="badge badge-indigo">{s.class_name}</span></td>
                  <td style={{ color: 'var(--slate-700)', fontWeight: 500 }}>{s.parent_name}</td>
                  <td style={{ color: 'var(--slate-400)', fontSize: 12 }}>{s.parent_email}</td>
                  <td>
                    {s.parent_pin ? (
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => openModal(s)}
                        style={{ gap: 6, color: 'var(--emerald)', fontWeight: 600, fontSize: 12 }}>
                        <IcQr size={14} stroke="var(--emerald)" strokeWidth={2} />
                        {s.parent_pin.slice(0,3)} {s.parent_pin.slice(3)}
                      </button>
                    ) : (
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => openModal(s)}
                        style={{ color: 'var(--slate-400)', fontSize: 12 }}>
                        <IcQr size={14} strokeWidth={1.5} />
                        Générer
                      </button>
                    )}
                  </td>
                  <td>
                    <button className="btn btn-danger btn-sm btn-icon" onClick={() => del(s.id)} title="Supprimer">
                      <IcTrash size={13} strokeWidth={2} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
